import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Sparkles, CheckCircle, Loader2, Edit2, Calendar as CalendarIcon, Clock, Link as LinkIcon, GripVertical, MoreVertical } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

// Build stages from start/end dates: days if <7, weeks+days if >=7
// Helper: returns Monday (week start) in YYYY-MM-DD for a given Date. Consistent ISO-like week boundary.
const weekStartISO = (d) => {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1) - day; // go back to Monday
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0, 0, 0, 0);
  return dt.toISOString().split('T')[0];
};

const buildStages = (start, end) => {
  if (!start || !end) return [];
  const s = new Date(start); const e = new Date(end);
  const diffDays = Math.ceil((e - s) / 86400000) + 1;
  if (diffDays <= 0) return [];

  // Group every day of the goal by its calendar-week (monday-start).
  // Even short goals (<7 días) are wrapped in their corresponding "Semana N" bucket
  // — only split into multiple buckets when days actually span different weeks.
  const buckets = new Map(); // weekStartISO -> { id, label, days: [] }
  for (let i = 0; i < diffDays; i++) {
    const d = new Date(s); d.setDate(s.getDate() + i);
    const wkStart = weekStartISO(d);
    if (!buckets.has(wkStart)) buckets.set(wkStart, { wkStart, days: [] });
    buckets.get(wkStart).days.push({
      date: d.toISOString().split('T')[0],
      label: d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
    });
  }
  // Order weeks chronologically and number them Semana 1, Semana 2, ...
  const ordered = Array.from(buckets.values()).sort((a, b) => a.wkStart.localeCompare(b.wkStart));
  return ordered.map((b, w) => ({
    id: `w-${w}`,
    type: 'week',
    label: `Semana ${w + 1}`,
    days: b.days.map((d, i) => ({ id: `w${w}-d${i}`, type: 'day', label: d.label, date: d.date })),
  }));
};

const daysLeft = (end) => {
  if (!end) return null;
  return Math.max(0, Math.ceil((new Date(end) - new Date()) / 86400000));
};

const GoalForm = ({ title, setTitle, desc, setDesc, startDate, setStartDate, endDate, setEndDate, steps, setSteps, onSubmit, theme, submitLabel }) => {
  const inputStyle = { background: theme.elevated, borderColor: theme.border };
  // Build available day options from start/end dates
  const stages = buildStages(startDate, endDate);
  const dayOptions = [];
  stages.forEach(st => {
    if (st.type === 'day') dayOptions.push({ date: st.date, label: st.label });
    else if (st.days) st.days.forEach(d => dayOptions.push({ date: d.date, label: `${st.label} · ${d.label}` }));
  });
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <Input data-testid="goal-title-input" placeholder="Título de la meta" value={title} onChange={(e) => setTitle(e.target.value)} className="text-white" style={inputStyle} />
      <Textarea placeholder="Descripción" value={desc} onChange={(e) => setDesc(e.target.value)} className="min-h-[60px] text-white" style={inputStyle} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Fecha inicio</label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-white" style={inputStyle} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Fecha fin</label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-white" style={inputStyle} />
        </div>
      </div>
      <div>
        <label className="text-sm mb-2 block" style={{ color: theme.textMuted }}>Pasos / Objetivos</label>
        {dayOptions.length > 0 && <p className="text-[10px] mb-2" style={{ color: theme.textMuted }}>Asigna cada paso a un día específico para verlo en su bloque</p>}
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <Input value={step.text} onChange={(e) => { const s = [...steps]; s[i] = { ...s[i], text: e.target.value }; setSteps(s); }}
              placeholder={`Paso ${i + 1}`} className="text-white flex-1" style={inputStyle} />
            {dayOptions.length > 0 && (
              <select value={step.day || ''} onChange={(e) => { const s = [...steps]; s[i] = { ...s[i], day: e.target.value || null }; setSteps(s); }}
                className="text-xs rounded h-9 px-2 border outline-none shrink-0 max-w-[180px]"
                style={{ background: theme.elevated, borderColor: theme.border, color: step.day ? theme.accent : theme.textMuted }}>
                <option value="">Sin asignar</option>
                {dayOptions.map(d => <option key={d.date} value={d.date}>{d.label}</option>)}
              </select>
            )}
            <button onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} className="p-2 shrink-0" style={{ color: theme.error }}><Trash2 size={16} /></button>
          </div>
        ))}
        <Button variant="outline" onClick={() => setSteps([...steps, { text: '', completed: false, day: null }])} className="w-full mt-1"
          style={{ borderColor: theme.border, color: theme.textSecondary }}><Plus size={16} className="mr-2" />Agregar Paso</Button>
      </div>
      <Button data-testid="goal-submit-btn" onClick={onSubmit} className="w-full text-white" style={{ background: theme.primary }}>{submitLabel}</Button>
    </div>
  );
};

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [linkDialog, setLinkDialog] = useState(null); // { goal, stepText, date, hour }
  const [expandedDay, setExpandedDay] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newSteps, setNewSteps] = useState([]);
  const [newAIGenerated, setNewAIGenerated] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editSteps, setEditSteps] = useState([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiStartDate, setAiStartDate] = useState('');
  const [aiEndDate, setAiEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const { theme } = useTheme();
  const { t } = useLanguage();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGoals(); }, []);
  const fetchGoals = async () => { try { setGoals((await api.get('/goals')).data); } catch (err) { console.debug(err); } };

  const resetNewForm = () => { setNewTitle(''); setNewDesc(''); setNewStart(''); setNewEnd(''); setNewSteps([]); setNewAIGenerated(false); };

  // Track which goals are expanded (showing all weeks). Default: collapsed (current week only).
  const [expandedGoals, setExpandedGoals] = useState(new Set());
  const toggleExpand = (goalId) => {
    setExpandedGoals(prev => {
      const n = new Set(prev);
      if (n.has(goalId)) n.delete(goalId); else n.add(goalId);
      return n;
    });
  };
  // Find current week index from stages
  const findCurrentStageIndex = (stages) => {
    const todayStr = new Date().toISOString().split('T')[0];
    for (let i = 0; i < stages.length; i++) {
      const st = stages[i];
      if (st.type === 'day' && st.date === todayStr) return i;
      if (st.type === 'week' && st.days?.length) {
        const dates = st.days.map(d => d.date).sort();
        if (todayStr >= dates[0] && todayStr <= dates[dates.length - 1]) return i;
      }
    }
    // If no current week found, return first upcoming or 0
    for (let i = 0; i < stages.length; i++) {
      const st = stages[i];
      const firstDate = st.type === 'day' ? st.date : st.days?.[0]?.date;
      if (firstDate && firstDate >= todayStr) return i;
    }
    return 0;
  };

  const openEdit = (goal) => {
    setEditingGoal(goal);
    setEditTitle(goal.title);
    setEditDesc(goal.description || '');
    setEditStart(goal.start_date || '');
    setEditEnd(goal.end_date || '');
    setEditSteps((goal.steps || []).map(s => typeof s === 'object' ? { ...s } : { text: s, completed: false }));
    setShowEditDialog(true);
  };

  const saveGoal = async () => {
    if (!newTitle) { toast.error('Ingresa un título'); return; }
    try {
      await api.post('/goals', {
        title: newTitle, description: newDesc,
        steps: newSteps.filter(s => s.text?.trim()),
        ai_generated: newAIGenerated,
        start_date: newStart || null, end_date: newEnd || null,
      });
      toast.success('Meta agregada');
      resetNewForm(); setShowAddDialog(false); fetchGoals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateGoal = async () => {
    if (!editTitle || !editingGoal) return;
    try {
      await api.put(`/goals/${editingGoal.id}`, {
        title: editTitle, description: editDesc,
        steps: editSteps.filter(s => s.text?.trim()),
        start_date: editStart || null, end_date: editEnd || null,
      });
      toast.success('Actualizada'); setShowEditDialog(false); setEditingGoal(null); fetchGoals();
    } catch { toast.error('Error'); }
  };

  const generateStepsWithAI = async () => {
    if (!aiPrompt) { toast.error('Describe tu meta'); return; }
    if (!aiStartDate || !aiEndDate) { toast.error('Indica fecha inicio y fin'); return; }
    setIsGenerating(true);
    try {
      const stages = buildStages(aiStartDate, aiEndDate);
      const dayList = [];
      stages.forEach(st => {
        if (st.type === 'day') dayList.push({ date: st.date, label: st.label });
        else if (st.days) st.days.forEach(d => dayList.push({ date: d.date, label: `${st.label} - ${d.label}` }));
      });
      const datesText = dayList.map((d, i) => `${i + 1}. ${d.date} (${d.label})`).join('\n');
      const fullPrompt = `Meta: ${aiPrompt}\n\nFechas disponibles entre ${aiStartDate} y ${aiEndDate}:\n${datesText}\n\nResponde con una lista numerada (máximo ${Math.min(dayList.length * 2, 30)} pasos) donde cada paso tenga formato: "[YYYY-MM-DD] descripción del paso". Distribuye los pasos en las fechas dadas de forma lógica.`;

      const { data } = await api.post('/ai/generate-goal-steps', { prompt: fullPrompt });
      const lines = data.response.split('\n').filter(l => l.trim() && /^\d+/.test(l.trim()));
      const steps = lines.map(l => {
        const cleaned = l.replace(/^\d+[\.\)]\s*/, '').trim();
        // extract [YYYY-MM-DD] if present
        const m = cleaned.match(/^\[(\d{4}-\d{2}-\d{2})\]\s*(.*)$/);
        if (m) {
          // Validate the date is in dayList
          const isValid = dayList.some(d => d.date === m[1]);
          return { text: m[2], completed: false, day: isValid ? m[1] : null };
        }
        return { text: cleaned, completed: false };
      });
      setNewTitle(aiPrompt);
      setNewDesc('Meta generada con IA');
      setNewStart(aiStartDate); setNewEnd(aiEndDate);
      setNewSteps(steps);
      setNewAIGenerated(true);
      toast.success(`${steps.length} pasos generados con fechas`);
      // Close the AI dialog first; defer opening the next dialog to prevent
      // simultaneous Radix portal mount/unmount which causes removeChild errors.
      setShowAIDialog(false);
      setAiPrompt(''); setAiStartDate(''); setAiEndDate('');
      setTimeout(() => { setShowAddDialog(true); }, 250);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setIsGenerating(false); }
  };

  const toggleStep = async (goalId, stepIndex) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const steps = [...goal.steps];
    steps[stepIndex] = { ...steps[stepIndex], completed: !steps[stepIndex].completed };
    try { await api.put(`/goals/${goalId}`, { title: goal.title, description: goal.description, steps, start_date: goal.start_date, end_date: goal.end_date }); fetchGoals(); } catch (err) { console.debug(err); }
  };

  const updateStepText = async (goalId, stepIndex, text) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const steps = [...goal.steps];
    steps[stepIndex] = { ...steps[stepIndex], text };
    try { await api.put(`/goals/${goalId}`, { title: goal.title, description: goal.description, steps, start_date: goal.start_date, end_date: goal.end_date }); fetchGoals(); } catch (err) { console.debug(err); }
  };

  const updateStepDay = async (goalId, stepIndex, day) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const steps = [...goal.steps];
    steps[stepIndex] = { ...steps[stepIndex], day };
    try { await api.put(`/goals/${goalId}`, { title: goal.title, description: goal.description, steps, start_date: goal.start_date, end_date: goal.end_date }); fetchGoals(); } catch (err) { console.debug(err); }
  };

  const deleteStep = async (goalId, stepIndex) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const steps = goal.steps.filter((_, i) => i !== stepIndex);
    try { await api.put(`/goals/${goalId}`, { title: goal.title, description: goal.description, steps, start_date: goal.start_date, end_date: goal.end_date }); fetchGoals(); } catch (err) { console.debug(err); }
  };

  const completeGoal = async (id) => {
    try { await api.put(`/goals/${id}/complete`); toast.success('¡Meta completada!'); fetchGoals(); } catch (err) { console.debug(err); }
  };

  const deleteGoal = async (id) => {
    try { await api.delete(`/goals/${id}`); toast.success('Eliminada'); fetchGoals(); } catch (err) { console.debug(err); }
  };

  return (
    <div data-testid="goals-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-2" style={{ color: theme.accent }}>{t('goals')}</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button className="text-white" style={{ background: theme.primary }}><Sparkles size={18} className="mr-2" />Asistencia IA</Button>
            </DialogTrigger>
            <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <DialogHeader><DialogTitle className="text-2xl" style={{ color: theme.accent }}>Generar Plan con IA</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Textarea placeholder="Describe tu meta..." value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                  className="min-h-[100px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Inicio</label>
                    <Input type="date" value={aiStartDate} onChange={(e) => setAiStartDate(e.target.value)}
                      className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Fin</label>
                    <Input type="date" value={aiEndDate} onChange={(e) => setAiEndDate(e.target.value)}
                      className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                  </div>
                </div>
                <p className="text-[10px]" style={{ color: theme.textMuted }}>La IA distribuirá los pasos en fechas específicas entre inicio y fin.</p>
                <Button onClick={generateStepsWithAI} disabled={isGenerating} className="w-full text-white" style={{ background: theme.primary }}>
                  {isGenerating ? <><Loader2 size={18} className="mr-2 animate-spin" />Generando...</> : 'Generar Plan con Fechas'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetNewForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-goal-btn" className="text-white" style={{ background: theme.primary }}><Plus size={18} className="mr-2" />Nueva Meta</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <DialogHeader><DialogTitle className="text-2xl" style={{ color: theme.accent }}>Agregar Meta</DialogTitle></DialogHeader>
              <GoalForm title={newTitle} setTitle={setNewTitle} desc={newDesc} setDesc={setNewDesc}
                startDate={newStart} setStartDate={setNewStart} endDate={newEnd} setEndDate={setNewEnd}
                steps={newSteps} setSteps={setNewSteps} onSubmit={saveGoal} theme={theme} submitLabel="Agregar Meta" />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) setEditingGoal(null); }}>
        <DialogContent className="max-w-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader><DialogTitle className="text-2xl" style={{ color: theme.accent }}>Editar Meta</DialogTitle></DialogHeader>
          <GoalForm title={editTitle} setTitle={setEditTitle} desc={editDesc} setDesc={setEditDesc}
            startDate={editStart} setStartDate={setEditStart} endDate={editEnd} setEndDate={setEditEnd}
            steps={editSteps} setSteps={setEditSteps} onSubmit={updateGoal} theme={theme} submitLabel="Guardar Cambios" />
        </DialogContent>
      </Dialog>

      {/* Goals List */}
      <div className="space-y-4">
        {goals.map((goal) => {
          const stepsArr = (goal.steps || []).map(s => typeof s === 'object' ? s : { text: s, completed: false });
          const completedCount = stepsArr.filter(s => s.completed).length;
          const progress = stepsArr.length ? Math.round((completedCount / stepsArr.length) * 100) : (goal.completed ? 100 : 0);
          const stages = buildStages(goal.start_date, goal.end_date);
          const remaining = daysLeft(goal.end_date);
          return (
            <div key={goal.id} data-testid={`goal-${goal.id}`} className={`rounded-lg overflow-hidden transition-all ${goal.completed ? 'opacity-60' : ''}`}
              style={{ background: theme.card, border: `1px solid ${theme.border}`, borderLeftWidth: '4px', borderLeftColor: goal.completed ? theme.success : theme.primary }}>
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xl font-medium" style={{ color: theme.accent, textDecoration: goal.completed ? 'line-through' : 'none' }}>{goal.title}</h3>
                      {goal.completed && <CheckCircle size={18} style={{ color: theme.success }} />}
                    </div>
                    {goal.description && <p className="text-sm mb-3" style={{ color: theme.textSecondary }}>{goal.description}</p>}

                    {/* Date metadata */}
                    {(goal.start_date || goal.end_date) && (
                      <div className="flex items-center gap-4 mb-3 flex-wrap text-xs">
                        {goal.start_date && (
                          <div className="flex items-center gap-1.5"><CalendarIcon size={12} style={{ color: theme.textMuted }} />
                            <span style={{ color: theme.textMuted }}>Inicio:</span>
                            <span style={{ color: theme.textPrimary }}>{new Date(goal.start_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        )}
                        {goal.end_date && (
                          <div className="flex items-center gap-1.5"><CalendarIcon size={12} style={{ color: theme.textMuted }} />
                            <span style={{ color: theme.textMuted }}>Fin:</span>
                            <span style={{ color: theme.textPrimary }}>{new Date(goal.end_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        )}
                        {remaining !== null && !goal.completed && (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full" style={{ background: remaining <= 3 ? `${theme.error}25` : `${theme.primary}25` }}>
                            <Clock size={12} style={{ color: remaining <= 3 ? theme.error : theme.accent }} />
                            <span className="font-semibold" style={{ color: remaining <= 3 ? theme.error : theme.accent }}>{remaining} {remaining === 1 ? 'día' : 'días'} restantes</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Progress bar */}
                    {stepsArr.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>{completedCount}/{stepsArr.length} completados</span>
                          <span className="text-xs font-semibold" style={{ color: progress >= 100 ? theme.success : theme.accent }}>{progress}%</span>
                        </div>
                        <div className="w-full h-1.5 rounded-full" style={{ background: theme.elevated }}>
                          <div className="h-1.5 rounded-full transition-all" style={{ background: progress >= 100 ? theme.success : theme.primary, width: `${progress}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(goal)} className="p-2" style={{ color: theme.textMuted }}><Edit2 size={16} /></button>
                    <button onClick={() => deleteGoal(goal.id)} className="p-2" style={{ color: theme.textMuted }}><Trash2 size={16} /></button>
                  </div>
                </div>

                {/* Steps with individual checkboxes, editable, deletable */}
                {stepsArr.length > 0 && stages.length === 0 && (() => {
                  const dayOptions = [];
                  return (
                    <div className="space-y-1 mt-3" style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '12px' }}>
                      {stepsArr.map((step, i) => (
                        <StepRow key={i} step={step} onToggle={() => toggleStep(goal.id, i)}
                          onTextChange={(t) => updateStepText(goal.id, i, t)}
                          onDayChange={(d) => updateStepDay(goal.id, i, d)}
                          onDelete={() => deleteStep(goal.id, i)} theme={theme} dayOptions={dayOptions} />
                      ))}
                    </div>
                  );
                })()}

                {/* PLANNER-STYLE BLOCK VIEW (Semana 1, Día 1, Día 2...) */}
                {stages.length > 0 && (() => {
                  const dayOptions = [];
                  stages.forEach(st => {
                    if (st.type === 'day') dayOptions.push({ date: st.date, label: st.label });
                    else if (st.days) st.days.forEach(d => dayOptions.push({ date: d.date, label: `${st.label} · ${d.label}` }));
                  });
                  // Unassigned steps
                  const unassigned = stepsArr.map((s, i) => ({ s, i })).filter(({ s }) => !s.day);
                  // Determine which stages to display based on expand state
                  const isExpanded = expandedGoals.has(goal.id);
                  const currentIdx = findCurrentStageIndex(stages);
                  const visibleStages = isExpanded ? stages : (stages[currentIdx] ? [stages[currentIdx]] : stages.slice(0, 1));
                  const hasMultipleStages = stages.length > 1;
                  return (
                    <div className="mt-4 space-y-4" style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '16px' }}>
                      {/* Expand/Compact toggle */}
                      {hasMultipleStages && (
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] uppercase tracking-widest" style={{ color: theme.textMuted }}>
                            {isExpanded ? `${stages.length} etapas` : `Mostrando: ${stages[currentIdx]?.label || stages[0]?.label}`}
                          </p>
                          <button onClick={() => toggleExpand(goal.id)}
                            className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full transition-all hover:scale-105"
                            style={{ background: `${theme.primary}25`, color: theme.accent, border: `1px solid ${theme.borderHover || theme.primary}40` }}>
                            {isExpanded ? '▲ Retraer' : `▼ Ver todas las etapas (${stages.length})`}
                          </button>
                        </div>
                      )}
                      {/* Unassigned steps strip */}
                      {unassigned.length > 0 && (
                        <div className="rounded-xl p-3" style={{ background: theme.elevated, border: `1px dashed ${theme.borderHover}` }}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: theme.textMuted }}>Sin asignar — arrástralos a un día</p>
                            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${theme.primary}25`, color: theme.accent }}>{unassigned.length}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {unassigned.map(({ s, i }) => (
                              <DraggableStep key={i} step={s} index={i} goalId={goal.id} goal={goal}
                                onTextChange={(t) => updateStepText(goal.id, i, t)}
                                onToggle={() => toggleStep(goal.id, i)}
                                onDelete={() => deleteStep(goal.id, i)}
                                theme={theme} compact />
                            ))}
                          </div>
                        </div>
                      )}

                      {visibleStages.map((stage, sIdx) => {
                        const days = stage.type === 'week' ? stage.days : [{ id: stage.id, type: 'day', label: stage.label, date: stage.date }];
                        return (
                          <div key={stage.id} className="rounded-2xl overflow-hidden"
                            style={{ background: '#0A0606', border: `1px solid ${theme.border}` }}>
                            {/* Week banner */}
                            <div className="px-6 py-4 rounded-t-2xl"
                              style={{ background: theme.gradient || `linear-gradient(90deg, ${theme.primary}, ${theme.primaryHover})`, boxShadow: `0 4px 20px ${theme.primary}40` }}>
                              <div className="flex items-center justify-between">
                                <h4 className="text-2xl font-light tracking-tight" style={{ color: '#fff' }}>{stage.label}</h4>
                                {stage.date && (
                                  <button onClick={() => linkToCalendar(goal, stage.label, stage.date)}
                                    className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full backdrop-blur-md hover:scale-105 transition-transform"
                                    style={{ background: 'rgba(0,0,0,0.3)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}>
                                    <LinkIcon size={11} />Calendario
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Day columns - horizontal scroll for >4 days */}
                            <div className="p-4">
                              <div className="overflow-x-auto pb-2 themed-scroll" style={{
                                '--scroll-thumb': `${theme.primary}55`,
                                '--scroll-track': `${theme.primary}15`,
                              }}>
                                <div className="flex gap-3" style={{ minWidth: 'min-content' }}>
                                {days.map((d, di) => {
                                  const stepsForDay = stepsArr
                                    .map((s, i) => ({ s, i }))
                                    .filter(({ s }) => s.day === d.date);
                                  const dateObj = d.date ? new Date(d.date + 'T00:00:00') : null;
                                  const isToday = dateObj && dateObj.toDateString() === new Date().toDateString();
                                  const isPast = dateObj && dateObj < new Date(new Date().toISOString().split('T')[0]);
                                  return (
                                    <div key={d.id}
                                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = `${theme.primary}25`; }}
                                      onDragLeave={(e) => { e.currentTarget.style.background = `${theme.primary}15`; }}
                                      onDrop={(e) => {
                                        e.preventDefault(); e.currentTarget.style.background = `${theme.primary}15`;
                                        const stepIdx = parseInt(e.dataTransfer.getData('stepIndex'));
                                        if (!isNaN(stepIdx)) updateStepDay(goal.id, stepIdx, d.date);
                                      }}
                                      onClick={(e) => {
                                        // Only expand if click was on the block itself (not on a step pill or button)
                                        if (e.target === e.currentTarget || e.target.classList?.contains('day-block-header')) {
                                          setExpandedDay({ goal, stage, day: d, di, stepsForDay });
                                        }
                                      }}
                                      className="rounded-xl p-4 min-h-[300px] transition-colors shrink-0 cursor-pointer hover:scale-[1.01]"
                                      style={{
                                        width: '260px',
                                        background: `${theme.primary}15`,
                                        border: isToday ? `1.5px solid ${theme.accent}` : `1px solid ${theme.border}`,
                                        opacity: isPast && stepsForDay.every(({ s }) => s.completed) ? 0.55 : 1,
                                      }}>
                                      <div className="day-block-header pointer-events-none">
                                        <p className="text-base font-semibold mb-1" style={{ color: '#fff', opacity: 0.95 }}>
                                          {stage.type === 'week' ? `Día ${di + 1}` : 'Día'}
                                        </p>
                                        {dateObj && (
                                          <p className="text-[12px] font-normal capitalize mb-3" style={{ color: 'rgba(255,255,255,0.65)' }}>
                                            {dateObj.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                                          </p>
                                        )}
                                      </div>
                                      <div className="space-y-1.5">
                                        {stepsForDay.slice(0, 4).map(({ s, i }) => (
                                          <DraggableStep key={i} step={s} index={i} goalId={goal.id} goal={goal}
                                            onTextChange={(t) => updateStepText(goal.id, i, t)}
                                            onToggle={() => toggleStep(goal.id, i)}
                                            onDelete={() => deleteStep(goal.id, i)}
                                            onAddToCalendar={() => linkToCalendar(goal, s.text, s.day)}
                                            theme={theme} />
                                        ))}
                                        {stepsForDay.length > 4 && (
                                          <button onClick={(e) => { e.stopPropagation(); setExpandedDay({ goal, stage, day: d, di, stepsForDay }); }}
                                            className="w-full text-center text-[10px] py-1.5 rounded-md mt-1 transition-colors"
                                            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)' }}>
                                            +{stepsForDay.length - 4} más · ver todo
                                          </button>
                                        )}
                                        {stepsForDay.length === 0 && (
                                          <p className="text-[12px] text-center italic py-6 day-block-header" style={{ color: 'rgba(255,255,255,0.4)' }}>Suelta un paso aquí</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {!goal.completed && (
                  <Button onClick={() => completeGoal(goal.id)} className="w-full mt-4 text-white" style={{ background: theme.success }}>
                    <CheckCircle size={18} className="mr-2" />Marcar como Completada
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {goals.length === 0 && (
          <div className="text-center py-20 rounded-lg" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <p style={{ color: theme.textSecondary }}>No tienes metas aún</p>
            <p className="text-xs mt-1" style={{ color: theme.textMuted }}>Crea tu primera meta con plazo</p>
          </div>
        )}
      </div>
      {/* Link to calendar with hour selection */}
      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader><DialogTitle style={{ color: theme.accent }}>Agregar al calendario</DialogTitle></DialogHeader>
          {linkDialog && (
            <div className="space-y-3">
              <div className="p-3 rounded-md" style={{ background: theme.elevated, borderLeft: `3px solid ${theme.primary}` }}>
                <p className="text-xs" style={{ color: theme.textMuted }}>Evento</p>
                <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>{linkDialog.goal.title}: {linkDialog.stepText}</p>
                <p className="text-xs mt-1" style={{ color: theme.accent }}>📅 {linkDialog.date}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Hora inicio</label>
                  <Input type="time" value={linkDialog.hour} onChange={(e) => setLinkDialog({ ...linkDialog, hour: e.target.value })}
                    className="text-white" style={{ background: theme.elevated, borderColor: theme.border, colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Duración (h)</label>
                  <Input type="number" min={1} max={12} value={linkDialog.duration} onChange={(e) => setLinkDialog({ ...linkDialog, duration: parseInt(e.target.value) || 1 })}
                    className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                </div>
              </div>
              <Button onClick={confirmLinkToCalendar} className="w-full text-white" style={{ background: theme.primary }}>Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expanded day block view (modal) */}
      <Dialog open={!!expandedDay} onOpenChange={(o) => !o && setExpandedDay(null)}>
        <DialogContent className="max-w-2xl" style={{ background: '#0A0606', border: `1px solid ${theme.border}`, padding: 0, overflow: 'hidden' }}>
          {expandedDay && (() => {
            const { goal, stage, day, di, stepsForDay } = expandedDay;
            const dateObj = day.date ? new Date(day.date + 'T00:00:00') : null;
            return (
              <div>
                <div className="px-6 py-5" style={{ background: theme.gradient || `linear-gradient(90deg, ${theme.primary}, ${theme.primaryHover})` }}>
                  <p className="text-xs uppercase tracking-[0.25em] opacity-80 mb-1" style={{ color: '#fff' }}>{stage.label}</p>
                  <h3 className="text-3xl font-light tracking-tight" style={{ color: '#fff' }}>
                    {stage.type === 'week' ? `Día ${di + 1}` : 'Día'}
                  </h3>
                  {dateObj && (
                    <p className="text-sm capitalize mt-1" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {dateObj.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
                <div className="p-6 max-h-[60vh] overflow-y-auto themed-scroll" style={{ '--scroll-thumb': `${theme.primary}55`, '--scroll-track': `${theme.primary}15` }}>
                  {stepsForDay.length === 0 ? (
                    <p className="text-center py-10 italic" style={{ color: theme.textMuted }}>No hay pasos asignados a este día</p>
                  ) : (
                    <div className="space-y-2">
                      {stepsForDay.map(({ s, i }) => (
                        <div key={i} className="rounded-lg p-3" style={{ background: theme.elevated, borderLeft: `3px solid ${s.completed ? theme.success : theme.primary}` }}>
                          <DraggableStep step={s} index={i} goalId={goal.id} goal={goal}
                            onTextChange={(t) => updateStepText(goal.id, i, t)}
                            onToggle={() => toggleStep(goal.id, i)}
                            onDelete={() => deleteStep(goal.id, i)}
                            onAddToCalendar={() => linkToCalendar(goal, s.text, s.day)}
                            theme={theme} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Independent step row with inline edit + day assignment
const StepRow = ({ step, onToggle, onTextChange, onDelete, onDayChange, theme, dayOptions }) => {
  const [local, setLocal] = useState(step.text);
  useEffect(() => { setLocal(step.text); }, [step.text]);
  return (
    <div className="flex items-center gap-2 py-1 px-2 rounded-md group">
      <Checkbox data-testid="step-checkbox" checked={step.completed} onCheckedChange={onToggle} />
      <Input value={local} onChange={(e) => setLocal(e.target.value)}
        onBlur={() => local !== step.text && onTextChange(local)}
        className="flex-1 text-sm border-0 h-7 px-1"
        style={{ background: 'transparent', color: step.completed ? theme.textMuted : theme.textPrimary, textDecoration: step.completed ? 'line-through' : 'none' }} />
      {dayOptions && dayOptions.length > 0 && (
        <select value={step.day || ''} onChange={(e) => onDayChange(e.target.value || null)}
          className="text-[10px] rounded h-6 px-1 border outline-none"
          style={{ background: theme.elevated, borderColor: theme.border, color: step.day ? theme.accent : theme.textMuted }}>
          <option value="">Sin fecha</option>
          {dayOptions.map(d => <option key={d.date} value={d.date}>{d.label}</option>)}
        </select>
      )}
      <button onClick={onDelete} className="p-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: theme.textMuted }}>
        <Trash2 size={12} />
      </button>
    </div>
  );
};

// Draggable step pill (planner-style block)
const DraggableStep = ({ step, index, goalId, goal, onTextChange, onToggle, onDelete, onAddToCalendar, theme, compact }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(step.text);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setLocal(step.text); }, [step.text]);
  const completed = step.completed;
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('stepIndex', index.toString()); e.dataTransfer.effectAllowed = 'move'; }}
      className={`group flex items-center gap-1.5 rounded-full ${compact ? 'px-2.5 py-1' : 'px-3 py-2'} cursor-grab active:cursor-grabbing transition-all hover:scale-[1.02]`}
      style={{
        background: completed ? `${theme.success}30` : 'rgba(255,255,255,0.10)',
        border: `1px solid ${completed ? theme.success : 'rgba(255,255,255,0.18)'}`,
        backdropFilter: 'blur(6px)',
        textDecoration: completed ? 'line-through' : 'none',
      }}>
      <GripVertical size={10} style={{ color: 'rgba(255,255,255,0.4)' }} className="shrink-0" />
      <button onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} rounded-full border flex items-center justify-center shrink-0`}
        style={{ borderColor: completed ? theme.success : 'rgba(255,255,255,0.5)', background: completed ? theme.success : 'transparent' }}>
        {completed && <span className="text-[8px] text-white">✓</span>}
      </button>
      {editing ? (
        <input value={local}
          autoFocus
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => { setEditing(false); if (local !== step.text) onTextChange(local); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
          className="text-[11px] bg-transparent border-0 outline-none flex-1 min-w-0"
          style={{ color: '#fff' }} />
      ) : (
        <span onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className={`${compact ? 'text-[11px]' : 'text-xs'} flex-1 truncate`}
          style={{ color: completed ? 'rgba(255,255,255,0.5)' : '#fff' }}>
          {step.text || 'Texto ej:'}
        </span>
      )}
      {/* 3-dot menu: Calendar + Delete */}
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button onClick={(e) => e.stopPropagation()}
            className="opacity-50 group-hover:opacity-100 transition-opacity shrink-0 hover:scale-110"
            style={{ color: 'rgba(255,255,255,0.85)' }}>
            <MoreVertical size={12} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-1" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          {onAddToCalendar && step.day && (
            <button onClick={() => { onAddToCalendar(); setMenuOpen(false); }}
              className="w-full text-left px-2.5 py-1.5 text-xs rounded-md flex items-center gap-2 hover:bg-white/5"
              style={{ color: theme.textPrimary }}>
              <CalendarIcon size={12} style={{ color: theme.accent }} /> Agregar al calendario
            </button>
          )}
          <button onClick={() => { onDelete(); setMenuOpen(false); }}
            className="w-full text-left px-2.5 py-1.5 text-xs rounded-md flex items-center gap-2 hover:bg-white/5"
            style={{ color: theme.error }}>
            <Trash2 size={12} /> Eliminar
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default Goals;
